import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Intervention, Statut } from '@/lib/types';

type StatusMutationInput = { id: string; statut: Statut; commentaire?: string };
type DurationMutationInput = { id: string; dureeEstimeeMinutes: number; reason?: string };
type SlotMutationInput = { id: string; creneauDebut: string; mecanicienId?: string };
type DeliveryMutationInput = { id: string; livraisonConfirmee: string };
type RepriseMutationInput = { id: string; mode: 'now' | 'schedule'; mecanicienId?: string; creneauDebut?: string; dureeEstimeeMinutes?: number };

/**
 * Une mutation d'intervention impacte les listes du planning (['interventions'])
 * MAIS AUSSI les créneaux libres (['slots']) et les blocs occupés de l'agenda
 * du formulaire (['day-interventions']). On invalide les trois pour qu'un
 * créneau pris/libéré apparaisse/disparaisse immédiatement, sans attendre les
 * refetchInterval. Le socket fait de même côté multi-postes.
 */
function invalidatePlanning(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['interventions'] });
  void qc.invalidateQueries({ queryKey: ['slots'] });
  void qc.invalidateQueries({ queryKey: ['day-interventions'] });
}

export function useReprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RepriseMutationInput) => {
      const { id, ...body } = input;
      return api.post<{ intervention: Intervention }>(`/interventions/${id}/reprise`, body);
    },
    onSuccess: () => {
      invalidatePlanning(qc); // ['interventions'] (couvre attente-pieces) + ['slots'] + ['day-interventions']
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useUpdateStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, statut, commentaire }: StatusMutationInput) =>
      api.patch<{ intervention: Intervention }>(`/interventions/${id}/status`, { statut, commentaire }),
    onSuccess: () => invalidatePlanning(qc),
  });
}

export function useUpdateDuration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dureeEstimeeMinutes, reason }: DurationMutationInput) =>
      api.patch<{ intervention: Intervention }>(`/interventions/${id}/duration`, {
        dureeEstimeeMinutes,
        reason,
      }),
    onSuccess: () => invalidatePlanning(qc),
  });
}

export function useUpdateSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, creneauDebut, mecanicienId }: SlotMutationInput) =>
      api.patch<{ intervention: Intervention }>(`/interventions/${id}/slot`, {
        creneauDebut,
        mecanicienId,
      }),
    onSuccess: () => invalidatePlanning(qc),
  });
}

export function useConfirmDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, livraisonConfirmee }: DeliveryMutationInput) =>
      api.patch<{ intervention: Intervention }>(`/interventions/${id}/delivery-confirmed`, {
        livraisonConfirmee,
      }),
    onSuccess: () => invalidatePlanning(qc),
  });
}

export function useUpdateNumeroServiceKtm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, numeroServiceKtm }: { id: string; numeroServiceKtm: string | null }) =>
      api.patch<{ intervention: Intervention }>(`/interventions/${id}/numero-service`, {
        numeroServiceKtm,
      }),
    onSuccess: () => invalidatePlanning(qc),
  });
}

// Payload partiel accepté par PUT /interventions/:id (= createSchema.partial()).
export type UpdateInterventionInput = {
  id: string;
  mecanicienId?: string;
  typeAgenda?: 'rapide' | 'mecanique' | 'lavage';
  nature?: string;
  description?: string;
  dureeEstimeeMinutes?: number;
  dateRdv?: string;       // YYYY-MM-DD
  creneauDebut?: string;  // ISO datetime (heure-mur = UTC)
  urgent?: boolean;
  kilometrageEntree?: number;
  notesInternes?: string;
  clientId?: string;
  motoId?: string;
};

export function useUpdateIntervention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateInterventionInput) =>
      api.put<{ intervention: Intervention }>(`/interventions/${id}`, patch),
    onSuccess: () => invalidatePlanning(qc),
  });
}
